// frontend/src/components/chatbot/FormattedText.tsx
import React, { useMemo } from 'react';
import EmailPopup from './EmailPopup';

interface FormattedTextProps {
  text: string;
  className?: string;
}

const HTML_TAG_RE =
  /<(p|div|br|strong|em|b|i|a|ul|ol|li|h[1-6]|span|code|pre|blockquote)\b[^>]*>/i;

const INLINE_RE =
  /`([^`]+)`|\*\*([^*]+)\*\*|\*([^*]+)\*|\[([^\]]+)\]\(([^)]+)\)|(\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}\b)/g;

const decodeHtmlEntities = (str: string): string =>
  str
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&nbsp;/g, ' ');

const htmlToMarkdown = (html: string): string =>
  decodeHtmlEntities(
    html
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<\/p>\s*<p[^>]*>/gi, '\n')
      .replace(/<p[^>]*>/gi, '')
      .replace(/<\/p>/gi, '')
      .replace(/<strong[^>]*>/gi, '**')
      .replace(/<\/strong>/gi, '**')
      .replace(/<b[^>]*>/gi, '**')
      .replace(/<\/b>/gi, '**')
      .replace(/<em[^>]*>/gi, '*')
      .replace(/<\/em>/gi, '*')
      .replace(/<i[^>]*>/gi, '*')
      .replace(/<\/i>/gi, '*')
      .replace(/<a[^>]*href=["']([^"']+)["'][^>]*>([^<]*)<\/a>/gi, '[$2]($1)')
      .replace(/<[^>]+>/g, '')
  );

const parseInline = (text: string, keyPrefix: string): React.ReactNode[] => {
  const nodes: React.ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let n = 0;

  while ((match = INLINE_RE.exec(text)) !== null) {
    if (match.index > lastIndex) {
      nodes.push(
        <span key={`${keyPrefix}-text-${n}`}>{text.slice(lastIndex, match.index)}</span>
      );
      n += 1;
    }

    if (match[1] !== undefined) {
      // inline code
      nodes.push(
        <code key={`${keyPrefix}-code-${n}`} className="chatbot-inline-code">
          {match[1]}
        </code>
      );
      n += 1;
    } else if (match[2] !== undefined) {
      // bold
      nodes.push(
        <strong key={`${keyPrefix}-bold-${n}`} className="chatbot-bold">
          {match[2]}
        </strong>
      );
      n += 1;
    } else if (match[3] !== undefined) {
      // italic
      nodes.push(
        <em key={`${keyPrefix}-italic-${n}`} className="chatbot-italic">
          {match[3]}
        </em>
      );
      n += 1;
    } else if (match[4] !== undefined) {
      // link [text](url)
      const url = match[5] || '';
      if (url.toLowerCase().startsWith('mailto:')) {
        let email = url;
        let subject = 'Pista app support';
        try {
          const mailUrl = new URL(url);
          email = mailUrl.pathname || match[4];
          subject = mailUrl.searchParams.get('subject') || subject;
        } catch {
          email = match[4];
        }
        nodes.push(
          <EmailPopup key={`${keyPrefix}-email-${n}`} email={email} subject={subject} />
        );
      } else {
        nodes.push(
          <a
            key={`${keyPrefix}-link-${n}`}
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="chatbot-link"
          >
            {match[4]}
          </a>
        );
      }
      n += 1;
    } else if (match[6] !== undefined) {
      // plain email
      nodes.push(
        <EmailPopup key={`${keyPrefix}-email-${n}`} email={match[6]} />
      );
      n += 1;
    }

    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    nodes.push(<span key={`${keyPrefix}-text-end`}>{text.slice(lastIndex)}</span>);
  }

  return nodes.length > 0 ? nodes : [text];
};

const parseBlocks = (text: string): React.ReactNode[] => {
  const lines = text.split('\n');
  const blocks: React.ReactNode[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    // Code fence
    if (line.startsWith('```')) {
      const lang = line.slice(3).trim();
      const code: string[] = [];
      let j = i + 1;
      while (j < lines.length && !lines[j].startsWith('```')) {
        code.push(lines[j]);
        j += 1;
      }
      const key = `block-${blocks.length}`;
      blocks.push(
        <pre key={key} className="chatbot-code-block">
          <code className={lang ? `language-${lang}` : undefined}>{code.join('\n')}</code>
        </pre>
      );
      i = j + 1;
      continue;
    }

    // Heading
    const headingMatch = line.match(/^(#{1,6})\s+(.+)$/);
    if (headingMatch) {
      const level = headingMatch[1].length;
      const content = headingMatch[2].trim();
      const tag = `h${Math.min(level + 2, 6)}` as keyof JSX.IntrinsicElements;
      const key = `block-${blocks.length}`;
      blocks.push(
        React.createElement(
          tag,
          { key, className: 'chatbot-heading' },
          parseInline(content, key)
        )
      );
      i += 1;
      continue;
    }

    // Unordered list
    if (/^[-*+]\s+/.test(line)) {
      const items: React.ReactNode[] = [];
      while (i < lines.length && /^[-*+]\s+/.test(lines[i])) {
        const itemKey = `block-${blocks.length}-item-${items.length}`;
        items.push(
          <li key={itemKey} className="chatbot-list-item">
            {parseInline(lines[i].replace(/^[-*+]\s+/, ''), itemKey)}
          </li>
        );
        i += 1;
      }
      blocks.push(
        <ul key={`block-${blocks.length}`} className="chatbot-list chatbot-list-unordered">
          {items}
        </ul>
      );
      continue;
    }

    // Ordered list
    if (/^\d+\.\s+/.test(line)) {
      const items: React.ReactNode[] = [];
      while (i < lines.length && /^\d+\.\s+/.test(lines[i])) {
        const itemKey = `block-${blocks.length}-item-${items.length}`;
        items.push(
          <li key={itemKey} className="chatbot-list-item">
            {parseInline(lines[i].replace(/^\d+\.\s+/, ''), itemKey)}
          </li>
        );
        i += 1;
      }
      blocks.push(
        <ol key={`block-${blocks.length}`} className="chatbot-list chatbot-list-ordered">
          {items}
        </ol>
      );
      continue;
    }

    // Empty line
    if (line.trim() === '') {
      i += 1;
      continue;
    }

    // Paragraph (combine consecutive non-empty lines with <br> for line breaks)
    const paraLines: string[] = [];
    while (i < lines.length && lines[i].trim() !== '') {
      paraLines.push(lines[i]);
      i += 1;
    }
    const key = `block-${blocks.length}`;
    const paraChildren: React.ReactNode[] = [];
    paraLines.forEach((paraLine, lineIndex) => {
      if (lineIndex > 0) {
        paraChildren.push(<br key={`${key}-br-${lineIndex}`} />);
      }
      paraChildren.push(
        ...parseInline(paraLine, `${key}-line-${lineIndex}`)
      );
    });
    blocks.push(
      <p key={key} className="chatbot-paragraph">
        {paraChildren}
      </p>
    );
  }

  return blocks;
};

const FormattedText: React.FC<FormattedTextProps> = ({ text, className = 'chatbot-formatted' }) => {
  const content = useMemo(() => {
    const raw = text || '';
    const prepared = HTML_TAG_RE.test(raw) ? htmlToMarkdown(raw) : raw;
    const normalized = prepared.replace(/\r\n?/g, '\n').trim();
    if (!normalized) return null;
    return parseBlocks(normalized);
  }, [text]);

  if (!content) return null;
  return <div className={className}>{content}</div>;
};

export default FormattedText;
